/**
 * useSolArkBackfill - Hook for managing Sol-Ark historical data backfill
 *
 * Provides:
 * - Single unit backfill
 * - Bulk backfill for all units with Sol-Ark
 * - Progress tracking and logging
 * - Cancellation support
 */

import { useState, useCallback, useRef } from 'react';
import { authenticatedFetch } from '@/lib/api';
import type {
  BackfillLogEntry,
  BackfillResult,
  BackfillProgress,
  BackfillOptions,
  UnitWithSolArk,
} from '@/types/backfill';

interface UseSolArkBackfillReturn {
  // State
  logs: BackfillLogEntry[];
  progress: BackfillProgress;
  isRunning: boolean;

  // Actions
  backfillSingleUnit: (unitId: string, options?: BackfillOptions) => Promise<BackfillResult>;
  backfillAllUnits: (options?: BackfillOptions) => Promise<void>;
  cancelBackfill: () => void;
  clearLogs: () => void;
}

const initialProgress: BackfillProgress = {
  currentUnit: null,
  currentIndex: 0,
  totalUnits: 0,
  completedUnits: [],
  failedUnits: [],
  skippedUnits: [],
  totalRecordsUpserted: 0,
  isRunning: false,
  isCancelled: false,
};

export function useSolArkBackfill(): UseSolArkBackfillReturn {
  const [logs, setLogs] = useState<BackfillLogEntry[]>([]);
  const [progress, setProgress] = useState<BackfillProgress>(initialProgress);
  const cancelledRef = useRef(false);

  const addLog = useCallback(
    (level: BackfillLogEntry['level'], message: string, unitCode?: string) => {
      setLogs((prev) => [
        ...prev,
        {
          timestamp: new Date(),
          level,
          message,
          unitCode,
        },
      ]);
    },
    []
  );

  const clearLogs = useCallback(() => {
    setLogs([]);
    setProgress(initialProgress);
  }, []);

  /**
   * Backfill a single unit's historical data
   */
  const backfillSingleUnit = useCallback(
    async (unitId: string, options?: BackfillOptions): Promise<BackfillResult> => {
      const startDate = options?.startDate || '2024-01-01';
      const endDate = options?.endDate || new Date().toISOString().split('T')[0];

      try {
        addLog('info', `Starting backfill from ${startDate} to ${endDate}...`);

        const response = await authenticatedFetch(`/api/v1/admin/units/${unitId}/backfill`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ startDate, endDate }),
        });

        const data = (await response.json()) as BackfillResult;

        if (!response.ok) {
          const errorMsg = data.error || 'Backfill failed';
          addLog('error', errorMsg, data.unit_code);
          return {
            success: false,
            unit_code: data.unit_code || 'unknown',
            error: errorMsg,
          };
        }

        if (data.skipped) {
          addLog('warning', `Skipped: ${data.reason}`, data.unit_code);
          return data;
        }

        addLog(
          'success',
          `Synced ${data.records_upserted || 0} records (${data.months_fetched || 0} months)`,
          data.unit_code
        );
        return data;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        addLog('error', errorMessage);
        return {
          success: false,
          unit_code: 'unknown',
          error: errorMessage,
        };
      }
    },
    [addLog]
  );

  /**
   * Fetch all units with Sol-Ark systems
   */
  const fetchSolArkUnits = useCallback(async (): Promise<UnitWithSolArk[]> => {
    try {
      const response = await authenticatedFetch('/api/v1/admin/units');
      if (!response.ok) {
        throw new Error('Failed to fetch units');
      }
      const data = await response.json();
      return data.units || [];
    } catch (error) {
      addLog('error', `Failed to fetch Sol-Ark units: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return [];
    }
  }, [addLog]);

  /**
   * Backfill all units with Sol-Ark systems
   */
  const backfillAllUnits = useCallback(
    async (options?: BackfillOptions): Promise<void> => {
      cancelledRef.current = false;

      setProgress({
        ...initialProgress,
        isRunning: true,
      });

      addLog('info', 'Starting bulk Sol-Ark backfill...');
      addLog('info', 'Fetching units with Sol-Ark systems...');

      const units = await fetchSolArkUnits();

      if (units.length === 0) {
        addLog('warning', 'No units with Sol-Ark systems found');
        setProgress((prev) => ({ ...prev, isRunning: false }));
        return;
      }

      addLog('info', `Found ${units.length} units with Sol-Ark`);

      setProgress((prev) => ({
        ...prev,
        totalUnits: units.length,
      }));

      const startDate = options?.startDate || '2024-01-01';
      const endDate = options?.endDate || new Date().toISOString().split('T')[0];
      addLog('info', `Date range: ${startDate} to ${endDate}`);

      for (let i = 0; i < units.length; i++) {
        // Check for cancellation
        if (cancelledRef.current) {
          addLog('warning', 'Backfill cancelled by user');
          setProgress((prev) => ({
            ...prev,
            isRunning: false,
            isCancelled: true,
          }));
          return;
        }

        const unit = units[i];
        setProgress((prev) => ({
          ...prev,
          currentUnit: unit.unit_code,
          currentIndex: i + 1,
        }));

        addLog('info', `Processing unit ${i + 1}/${units.length}...`, unit.unit_code);

        const result = await backfillSingleUnit(unit.id, options);

        if (result.skipped) {
          setProgress((prev) => ({
            ...prev,
            skippedUnits: [...prev.skippedUnits, unit.unit_code],
          }));
        } else if (result.success) {
          setProgress((prev) => ({
            ...prev,
            completedUnits: [...prev.completedUnits, unit.unit_code],
            totalRecordsUpserted: prev.totalRecordsUpserted + (result.records_upserted || 0),
          }));
        } else {
          setProgress((prev) => ({
            ...prev,
            failedUnits: [...prev.failedUnits, unit.unit_code],
          }));
        }

        // Small delay between requests to avoid overwhelming the API
        if (i < units.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      setProgress((prev) => ({
        ...prev,
        currentUnit: null,
        isRunning: false,
      }));

      // Summary log
      addLog('info', '--- Backfill Complete ---');
      addLog(
        'info',
        `Completed: ${progress.completedUnits.length + 1} | Skipped: ${progress.skippedUnits.length} | Failed: ${progress.failedUnits.length}`
      );
      addLog('success', `Total records synced: ${progress.totalRecordsUpserted}`);
    },
    [addLog, backfillSingleUnit, fetchSolArkUnits, progress]
  );

  const cancelBackfill = useCallback(() => {
    cancelledRef.current = true;
    addLog('warning', 'Cancellation requested...');
  }, [addLog]);

  return {
    logs,
    progress,
    isRunning: progress.isRunning,
    backfillSingleUnit,
    backfillAllUnits,
    cancelBackfill,
    clearLogs,
  };
}
